import { forwardRef, useState } from 'react';
import type { WidgetProps } from '@rjsf/utils';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import type { Asset } from '../../Pages/Section/hooks/useGetAssets';
import { useUploadAsset } from '../../Pages/Section/hooks/useUploadAsset';
import { AssetPickerModal } from '../AssetPickerModal';
import { FileUploadModal } from '../FileUploadModal';
import { ImageCropModal } from '../ImageCropModal';

interface ImageItem {
  assetId: string;
  alt?: string;
  thumbnailUrl: string;
  imageUrl: string;
}

interface ImageManagerOptions {
  sources?: string[];
  sortable?: boolean;
  addButtonLabel?: string;
  aspectRatio?: string;
}

function buildDefaultFileName(originalName: string, aspectRatio?: string): string {
  const lastDot = originalName.lastIndexOf('.');
  const base = lastDot >= 0 ? originalName.slice(0, lastDot) : originalName;
  const ext = lastDot >= 0 ? originalName.slice(lastDot) : '';
  const suffix = aspectRatio ? `-${aspectRatio.replace(':', 'x')}` : '';
  return `${base}${suffix}${ext}`;
}

const SOURCE_LABELS: Record<string, string> = {
  disk: 'From disk',
  assets: 'From assets',
};

interface ImageRowProps {
  item: ImageItem;
  onDelete: () => void;
  handle?: ReactNode;
  style?: CSSProperties;
}

const ImageRow = forwardRef<HTMLDivElement, ImageRowProps>(function ImageRow(
  { item, onDelete, handle, style },
  ref
): ReactElement {
  return (
    <div
      ref={ref}
      style={style}
      className="vtwo:flex vtwo:items-center vtwo:gap-2 vtwo:px-4 vtwo:py-2 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
    >
      {handle}
      <Box className="vtwo:w-16 vtwo:h-16 vtwo:rounded-lg vtwo:overflow-hidden vtwo:shrink-0 vtwo:bg-gray-100">
        {item.thumbnailUrl && (
          <img
            src={item.thumbnailUrl}
            alt={item.alt ?? ''}
            className="vtwo:w-full vtwo:h-full vtwo:object-cover"
          />
        )}
      </Box>
      <IconButton size="small" className="vtwo:ml-auto" onClick={onDelete}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
});

interface SortableImageRowProps {
  id: string;
  item: ImageItem;
  onDelete: () => void;
}

function SortableImageRow({ id, item, onDelete }: SortableImageRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ImageRow
      ref={setNodeRef}
      item={item}
      onDelete={onDelete}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      handle={
        <Box
          {...attributes}
          {...listeners}
          className="vtwo:flex vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
        >
          <DragIndicatorIcon className="vtwo:text-gray-400 vtwo:text-[20px]" />
        </Box>
      }
    />
  );
}

export function ImageManagerWidget(props: WidgetProps): ReactElement {
  const { value, onChange, uiSchema } = props;

  const items = (value as ImageItem[] | undefined) ?? [];
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState<string>('');
  const [cropKey, setCropKey] = useState(0);
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { mutate: uploadAsset, isPending } = useUploadAsset();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const {
    sources = [],
    sortable = false,
    addButtonLabel = 'Add image',
    aspectRatio,
  } = (uiSchema?.['ui:options'] ?? {}) as ImageManagerOptions;

  const canReorder = sortable && items.length > 1;

  function handleSourceClick(source: string) {
    setAnchorEl(null);
    if (source === 'disk') {
      setUploadModalOpen(true);
    } else if (source === 'assets') {
      setAssetPickerOpen(true);
    }
  }

  function handleFileSelected(file: File) {
    setUploadModalOpen(false);
    setCropFileName(buildDefaultFileName(file.name, aspectRatio));
    setCropKey((k) => k + 1);
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  function handleCropDone(file: File) {
    setCropImageSrc(null);
    uploadAsset(
      { file, organisationId },
      {
        onSuccess: ({ id, thumbnailUrl, url }) => {
          onChange([...items, { assetId: id, thumbnailUrl, alt: '', imageUrl: url }]);
        },
      }
    );
  }

  function handleAssetSelect(asset: Asset) {
    onChange([
      ...items,
      { assetId: asset.id, thumbnailUrl: asset.thumbnailUrl, alt: '', imageUrl: asset.url },
    ]);
    setAssetPickerOpen(false);
  }

  function handleDelete(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.assetId === String(active.id));
    const newIndex = items.findIndex((item) => item.assetId === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onChange(arrayMove(items, oldIndex, newIndex));
  }

  let imageRows: ReactNode;
  if (canReorder) {
    imageRows = (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={items.map((item) => item.assetId)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableImageRow
              key={item.assetId}
              id={item.assetId}
              item={item}
              onDelete={() => handleDelete(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  } else {
    imageRows = items.map((item, index) => (
      <ImageRow key={item.assetId} item={item} onDelete={() => handleDelete(index)} />
    ));
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {imageRows}

      <Button
        variant="outlined"
        startIcon={isPending ? <CircularProgress size={16} /> : <AddIcon />}
        fullWidth
        disabled={isPending}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="vtwo:normal-case vtwo:text-gray-600 vtwo:border-plus-border vtwo:py-2 vtwo:rounded-lg"
      >
        {addButtonLabel}
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {sources.map((source) => (
          <MenuItem key={source} onClick={() => handleSourceClick(source)}>
            {SOURCE_LABELS[source] ?? source}
          </MenuItem>
        ))}
      </Menu>

      <FileUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onFileSelected={handleFileSelected}
      />

      <ImageCropModal
        key={cropKey}
        open={Boolean(cropImageSrc)}
        imageSrc={cropImageSrc ?? ''}
        aspectRatio={aspectRatio}
        defaultFileName={cropFileName}
        onClose={() => setCropImageSrc(null)}
        onCropDone={handleCropDone}
      />

      <AssetPickerModal
        open={assetPickerOpen}
        onClose={() => setAssetPickerOpen(false)}
        onSelect={handleAssetSelect}
        organisationId={organisationId}
        aspectRatio={aspectRatio}
      />
    </Box>
  );
}
